import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import { ProfileHintId, ProfileHintPlacement, profileHintById } from './profileHints';

interface ProfileHintProps {
    id: string;
    action: string;
    reason: string;
    show: boolean;
    /** bottom: under the control. Anything else: above and to the right, clear of the control. */
    placement?: ProfileHintPlacement;
    onDismiss: () => void;
    className?: string;
    children: ReactNode;
}

interface ProfileFieldHintProps {
    id: ProfileHintId;
    active: boolean;
    onDismiss: () => void;
    children: ReactNode;
}

interface AnchorBox {
    top: number;
    right: number;
    bottom: number;
    left: number;
    width: number;
}

const HINT_WIDTH = 248;

const measureAnchor = (root: HTMLElement | null): AnchorBox | null => {
    const target = root?.firstElementChild;
    if (!(target instanceof HTMLElement)) return null;

    const rect = target.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return null;

    const scroller = document.getElementById('app-scroll-container');
    if (scroller) {
        const view = scroller.getBoundingClientRect();
        if (rect.bottom < view.top || rect.top > view.bottom) return null;
    }

    return {
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
    };
};

const sameBox = (a: AnchorBox, b: AnchorBox) =>
    a.top === b.top && a.right === b.right && a.bottom === b.bottom && a.left === b.left;

const hintPosition = (placement: ProfileHintPlacement | undefined, box: AnchorBox): { top: number; left: number; transform: string } => {
    const gap = 10;
    if (placement === 'bottom') {
        return {
            top: box.bottom + gap,
            left: box.left + box.width / 2,
            transform: 'translateX(-50%)',
        };
    }

    const left = Math.min(box.right + gap, Math.max(gap, window.innerWidth - gap - HINT_WIDTH));
    return {
        top: box.top - gap,
        left,
        transform: 'translateY(-100%)',
    };
};

/**
 * Floats a tip beside its target. The wrapper is display:contents so the photo and buttons stay put.
 * The tip stays up until the field is filled or the person snoozes it.
 */
function ProfileHint({ id, action, reason, show, placement, onDismiss, className = '', children }: ProfileHintProps) {
    const targetRef = useRef<HTMLDivElement>(null);
    const [anchor, setAnchor] = useState<AnchorBox | null>(null);

    const activate = () => {
        targetRef.current?.querySelector<HTMLElement>('button, label')?.click();
    };

    useLayoutEffect(() => {
        if (!show) {
            setAnchor(null);
            return;
        }

        let frame = 0;
        const update = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const next = measureAnchor(targetRef.current);
                setAnchor((current) => {
                    if (!next && !current) return current;
                    if (next && current && sameBox(next, current)) return current;
                    return next;
                });
            });
        };

        update();
        const scroller = document.getElementById('app-scroll-container');
        const target = targetRef.current?.firstElementChild;
        const observer = target ? new ResizeObserver(update) : null;
        scroller?.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        if (target) observer?.observe(target);

        return () => {
            cancelAnimationFrame(frame);
            scroller?.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            observer?.disconnect();
        };
    }, [show]);

    return (
        <div ref={targetRef} className={`contents ${className}`}>
            {children}
            {show && anchor && createPortal(
                <div
                    data-testid={`profile-hint-${id}`}
                    className="fixed z-40 pointer-events-auto"
                    style={hintPosition(placement, anchor)}
                >
                    <div className="relative flex items-start gap-1.5 max-w-[248px] rounded-2xl bg-purple-light text-white shadow-lg shadow-purple-light/30 pl-3 pr-1.5 py-2">
                        {placement === 'bottom' && (
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-b-purple-light" aria-hidden />
                        )}
                        <span className="relative mt-1 flex h-2 w-2 shrink-0" aria-hidden>
                            <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 motion-safe:animate-ping dark:bg-white" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-white dark:bg-white" />
                        </span>
                        <button type="button" onClick={activate} className="group min-w-0 text-left">
                            <span className="block text-sm font-bold leading-tight group-hover:underline">{action}</span>
                            <span className="mt-0.5 block text-xs font-medium leading-tight text-white/80">{reason}</span>
                        </button>
                        <button
                            type="button"
                            onClick={onDismiss}
                            aria-label="Dismiss tip"
                            className="shrink-0 rounded-full p-1 text-white/80 hover:bg-white/15 hover:text-white"
                        >
                            <FiX className="h-3.5 w-3.5" />
                        </button>
                        {placement !== 'bottom' && (
                            <span className="absolute top-full left-3 border-[6px] border-transparent border-t-purple-light" aria-hidden />
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export function ProfileFieldHint({ id, active, onDismiss, children }: ProfileFieldHintProps) {
    const hint = profileHintById(id);
    return (
        <ProfileHint
            id={hint.id}
            action={hint.action}
            reason={hint.reason}
            placement={hint.placement}
            show={active}
            onDismiss={onDismiss}
        >
            {children}
        </ProfileHint>
    );
}

export default ProfileHint;
